import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  getPostTranslationStatus,
  translatePostCaption,
} from "../services/postTranslationApi";
import {
  isCaptionTranslationEligible,
  isPostTypeTranslationEligible,
  normalizePostTranslationLanguage,
} from "../utils/postTranslationEligibility";
import { detectPostCaptionLanguage } from "../utils/postCaptionLanguageDetection";

const HIDE_AFTER_ERROR_CODES = new Set([
  "post_translation_disabled",
  "post_translation_target_language_unsupported",
  "post_translation_caption_missing",
  "post_translation_caption_not_translatable",
  "post_translation_caption_too_long",
  "post_translation_caption_changed",
  "post_not_found",
]);

function entryKey(postId, caption, targetLanguage) {
  return JSON.stringify([String(postId || ""), caption, targetLanguage]);
}

function idleEntry() {
  return {
    status: "idle",
    translatedText: "",
    sourceLanguage: null,
  };
}

export default function usePostCaptionTranslation({
  postId,
  postType,
  caption,
}) {
  const { t, i18n } = useTranslation();
  const [capability, setCapability] = useState(null);
  const [entries, setEntries] = useState(() => new Map());
  const inFlightKeys = useRef(new Set());
  const controllers = useRef(new Map());
  const mounted = useRef(true);
  const identity = `${String(postId || "")}\u0000${String(caption ?? "")}`;
  const previousIdentity = useRef(identity);

  const targetLanguage = normalizePostTranslationLanguage(
    i18n.resolvedLanguage || i18n.language
  );
  const detectedCaptionLanguage = useMemo(
    () => detectPostCaptionLanguage(caption),
    [caption]
  );
  const sameLanguageLocally =
    Boolean(targetLanguage) && detectedCaptionLanguage === targetLanguage;
  const key = useMemo(
    () => entryKey(postId, caption, targetLanguage),
    [caption, postId, targetLanguage]
  );
  const entry = entries.get(key) || idleEntry();

  useEffect(() => {
    mounted.current = true;
    let active = true;
    getPostTranslationStatus().then((status) => {
      if (active && mounted.current) {
        setCapability(status);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(
    () => () => {
      mounted.current = false;
      controllers.current.forEach((controller) => controller.abort());
      controllers.current.clear();
      inFlightKeys.current.clear();
    },
    []
  );

  useEffect(() => {
    if (previousIdentity.current === identity) return;
    previousIdentity.current = identity;
    controllers.current.forEach((controller) => controller.abort());
    controllers.current.clear();
    inFlightKeys.current.clear();
    setEntries(new Map());
  }, [identity]);

  const capabilityReady = Boolean(
    capability?.enabled &&
      targetLanguage &&
      capability.supportedLanguages.includes(targetLanguage)
  );
  const contentEligible =
    isPostTypeTranslationEligible(postType) &&
    isCaptionTranslationEligible(caption) &&
    !sameLanguageLocally;
  const translationAvailable =
    capabilityReady &&
    contentEligible &&
    entry.status !== "sameLanguage" &&
    entry.status !== "suppressed";

  const updateEntry = useCallback(
    (nextEntry) => {
      if (!mounted.current) return;
      setEntries((current) => {
        const next = new Map(current);
        next.set(key, nextEntry);
        return next;
      });
    },
    [key]
  );

  const requestTranslation = useCallback(async () => {
    if (
      sameLanguageLocally ||
      !translationAvailable ||
      entry.status === "loading" ||
      inFlightKeys.current.has(key)
    ) {
      return;
    }

    const controller = new AbortController();
    inFlightKeys.current.add(key);
    controllers.current.set(key, controller);
    updateEntry({ ...idleEntry(), status: "loading" });

    try {
      const result = await translatePostCaption(postId, targetLanguage, {
        signal: controller.signal,
      });
      if (!mounted.current || controller.signal.aborted) return;
      if (result.postId !== String(postId) || result.targetLanguage !== targetLanguage) {
        updateEntry({ ...idleEntry(), status: "error" });
      } else if (result.sameLanguage) {
        updateEntry({
          status: "sameLanguage",
          translatedText: "",
          sourceLanguage: result.sourceLanguage,
        });
      } else {
        updateEntry({
          status: "translated",
          translatedText: result.translatedText,
          sourceLanguage: result.sourceLanguage,
        });
      }
    } catch (error) {
      if (!mounted.current || controller.signal.aborted) return;
      updateEntry({
        ...idleEntry(),
        status: HIDE_AFTER_ERROR_CODES.has(error?.code)
          ? "suppressed"
          : "error",
      });
    } finally {
      inFlightKeys.current.delete(key);
      controllers.current.delete(key);
    }
  }, [
    entry.status,
    key,
    postId,
    sameLanguageLocally,
    targetLanguage,
    translationAvailable,
    updateEntry,
  ]);

  const showOriginal = useCallback(() => {
    if (!entry.translatedText) return;
    updateEntry({ ...entry, status: "showingOriginal" });
  }, [entry, updateEntry]);

  const showTranslation = useCallback(() => {
    if (!entry.translatedText) return;
    updateEntry({ ...entry, status: "translated" });
  }, [entry, updateEntry]);

  return {
    t,
    targetLanguage,
    capabilityLoading: capability === null,
    translationAvailable,
    status: entry.status,
    sourceLanguage: entry.sourceLanguage,
    visibleText:
      entry.status === "translated" ? entry.translatedText : caption,
    hasTranslation: Boolean(entry.translatedText),
    requestTranslation,
    retryTranslation: requestTranslation,
    showOriginal,
    showTranslation,
  };
}
